import { context } from '@opentelemetry/api'
import * as Sentry from '@sentry/browser'
import createClient, { ClientOptions } from 'openapi-fetch'

import { globalActiveContext } from '~/hooks/useOtelTracker/Loading/globalActiveContext'
import { BACKGROUND_FLOW_CONTEXT_KEY, trackedFetch } from '~/utils/observability'
import { normalizePathname } from '~/utils/observability/tracking/utils/normalizePathname'

/**
 * Sentry's fetch instrumentation only records `http.client` spans when an active parent exists.
 * Pageload/navigation idle spans are that parent during initial load; after they end, background
 * refetches would otherwise be dropped. `startNewTrace` only resets the trace id, so we still
 * start an `app.request` root for Sentry to parent the automatic `http.client` span.
 */
function trackedFetchWithSentryParent(request: Request) {
    if (Sentry.getActiveSpan()) return trackedFetch(request)

    return Sentry.startNewTrace(() =>
        Sentry.startSpan({ name: `${request.method} ${normalizePathname(request.url)}`, op: 'app.request' }, () => trackedFetch(request))
    )
}

// Creates a base OpenAPI client with default configuration.
export function createBaseClient<Paths extends object>(clientOptions?: ClientOptions) {
    const client = createClient<Paths>({
        mode: 'cors',
        credentials: 'include',
        /**
         * Ensures Otel fetch spans are correctly parented based on the execution context:
         * - Loading spans (UI-blocking): use `globalActiveContext` managed by the loading span
         * - Background spans (Non-blocking, e.g. surgery booking): use `context.active()` from the current Zone
         *
         * `trackedFetch` wraps the call to record request duration and status as Otel metrics. It also does
         * an explicit lookup on the global object (i.e. `globalThis`) to ensure it picks up any patches to fetch.
         */
        fetch: request => {
            const isBackgroundFlow = context.active().getValue(BACKGROUND_FLOW_CONTEXT_KEY)
            const ctxToUse = isBackgroundFlow ? context.active() : (globalActiveContext ?? context.active())
            return context.with(ctxToUse, () => trackedFetchWithSentryParent(request))
        },
        ...clientOptions,
    })

    // IMPORTANT: middleware with `onRequest` breaks Otel Zone propagation for fetch calls,
    // which prevents end-to-end tracing of background spans that rely on this.

    return client
}
